"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";

import type { IndustryPreset } from "@/shared/types/industryPreset";
import type { TezamPlanCode } from "@/shared/types/onboard.types";
import { getIndustryPresetByPathname } from "@/shared/utils/industryPresetByPath";
import { getRequestTypeByPathname, writeLeadIntent, type LeadIntent } from "@/shared/utils/leadIntent";

export type OpenSaasModalOptions = {
    leadSource?: string;
    /** Override pathname-based niche (homepage switcher) */
    industryPreset?: IndustryPreset;
    campaignCode?: string;
};

export function useOnboardModal() {
    const pathname = usePathname() ?? "/";
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [leadIntent, setLeadIntent] = useState<LeadIntent>({});

    const openSaasModal = (
        planCode: TezamPlanCode,
        tariffName: string,
        leadSourceOrOptions?: string | OpenSaasModalOptions,
    ) => {
        const options: OpenSaasModalOptions =
            typeof leadSourceOrOptions === "string"
                ? { leadSource: leadSourceOrOptions }
                : (leadSourceOrOptions ?? {});

        const nextIntent = writeLeadIntent({
            tariff: tariffName,
            requestType: getRequestTypeByPathname(pathname),
            pagePath: pathname,
            industryPreset: options.industryPreset ?? getIndustryPresetByPathname(pathname),
            leadSource: options.leadSource,
            onboard: {
                mode: "onboard",
                planCode,
                metadata: options.campaignCode ? { campaign: options.campaignCode } : undefined,
            },
        });

        setLeadIntent(nextIntent);
        setIsModalOpen(true);
    };

    const openConsultationModal = (tariffName: string, leadSource?: string) => {
        const nextIntent = writeLeadIntent({
            tariff: tariffName,
            requestType: getRequestTypeByPathname(pathname),
            pagePath: pathname,
            leadSource,
            onboard: { mode: "consultation" },
        });

        setLeadIntent(nextIntent);
        setIsModalOpen(true);
    };

    /** Lead form with industry preset preselected (homepage niche switcher). */
    const openIndustryLeadModal = (
        industryPreset: IndustryPreset,
        options?: { tariff?: string; leadSource?: string },
    ) => {
        const nextIntent = writeLeadIntent({
            tariff: options?.tariff,
            requestType: getRequestTypeByPathname(pathname),
            pagePath: pathname,
            industryPreset,
            leadSource: options?.leadSource,
        });

        setLeadIntent(nextIntent);
        setIsModalOpen(true);
    };

    const closeModal = () => setIsModalOpen(false);

    return {
        isModalOpen,
        leadIntent,
        openSaasModal,
        openConsultationModal,
        openIndustryLeadModal,
        closeModal,
    };
}
